import type { CdArtifactRecord, CdReleasePlan } from './cdDemoApi';
import { clickCiApiUrl } from './apiUrl';

export type TestCategory = 'Model' | 'Expressions' | 'Selections' | 'Visuals' | 'Reload' | 'Regression';
export type TestStatus = 'pending' | 'running' | 'passed' | 'failed';
export const CURRENT_TEST_GENERATOR = 'app-content-generator-v2' as const;

export interface ArtifactTestCase {
  id: string;
  category: TestCategory;
  title: string;
  purpose: string;
  evidenceSource: 'artifact' | 'qlik-api' | 'qlik-engine' | 'reload' | 'round-trip';
  assertion: 'inventory' | 'expression' | 'selection' | 'visual' | 'reload' | 'checksum' | 'round-trip' | 'revision';
  targetId?: string;
  expected?: Record<string, string | number | boolean>;
}

export interface ArtifactTestPlan {
  id: string;
  releasePlanId: string;
  artifactId: string;
  targetAppName: string;
  environment: CdReleasePlan['environment'];
  version: string;
  revision: string;
  artifactSha256: string;
  generator: 'app-content-generator-v1' | typeof CURRENT_TEST_GENERATOR;
  generatedAt: string;
  narrative: string;
  inventory: {
    appObjects: number; visualObjects: number; measures: number; dimensions: number;
    variables: number; bookmarks: number; stories: number; scriptSections: number;
  };
  cases: ArtifactTestCase[];
}

export interface ArtifactTestResult extends ArtifactTestCase {
  status: 'passed' | 'failed';
  evidence: string;
  durationMs: number;
  executionMode: 'structural' | 'engine-executed' | 'artifact-executed' | 'exact-sha-cache' | 'controlled-injection';
}

export interface ArtifactTestStep {
  key: string;
  title: string;
  purpose: string;
  status: TestStatus;
  detail: string;
  startedAt?: string;
  completedAt?: string;
}

export interface ArtifactTestRun {
  id: string;
  planId: string;
  scenario: 'success' | 'selection_failure';
  status: 'queued' | 'running' | 'passed' | 'failed';
  createdAt: string;
  completedAt: string | null;
  buildAppId: string | null;
  error: string | null;
  steps: ArtifactTestStep[];
  results: ArtifactTestResult[];
  report: null | {
    confidence: number;
    risk: 'Low' | 'Medium' | 'High';
    summary: string;
    businessImpact: string;
    suggestedAction: string;
    deterministicEvidence: string[];
    demoInjectedFailure: boolean;
    evidenceProfile: 'engine-selection-v1';
    structuralEvidenceRunId?: string;
    aiInterpretation?: {
      status: 'generated' | 'fallback';
      provider: 'openai' | 'deterministic';
      model: string;
      generatedAt: string;
      promptVersion: 'quality-evidence-v1';
      evidenceDigest: string;
      evidenceReferences: string[];
      responseId?: string;
      inputTokens?: number;
      outputTokens?: number;
      fallbackReason?: string;
    };
  };
}

export interface ArtifactTestPortfolioRow {
  releasePlan: CdReleasePlan;
  artifact: CdArtifactRecord | null;
  testPlan: ArtifactTestPlan | null;
  latestRun: ArtifactTestRun | null;
}

interface ResultEnvelope<T> { result?: T; message?: string }
const apiUrl = clickCiApiUrl;

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiUrl}${url}`, init);
  const payload = await response.json() as ResultEnvelope<T>;
  if (!response.ok || payload.result === undefined) throw new Error(payload.message ?? `Request failed (${response.status})`);
  return payload.result;
}

export const testDemoApi = {
  listPortfolio: () => request<{ rows: ArtifactTestPortfolioRow[] }>('/api/demo-test/plans'),
  generatePlan: (releasePlanId: string, artifactId: string) => request<ArtifactTestPlan>('/api/demo-test/plans', {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ releasePlanId, artifactId }),
  }),
  getPlan: (planId: string) => request<{ plan: ArtifactTestPlan; runs: ArtifactTestRun[] }>(`/api/demo-test/plans/${planId}`),
  startRun: (planId: string, scenario: ArtifactTestRun['scenario']) => request<ArtifactTestRun>(`/api/demo-test/plans/${planId}/runs`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ scenario }),
  }),
  getRun: (runId: string) => request<ArtifactTestRun>(`/api/demo-test/runs/${runId}`),
};
